import { ReactNode, useMemo } from 'react';
import { Conversation, useCanUpdateConversation } from 'src/graphql';
import { CustomAttributesSidePanelProvider } from 'src/graphql/entities/custom-attributes/side-panel-provider';
import { useConversationPropertiesForm } from 'src/pages/Conversations/SidePanel/ConversationDetails/use-conversation-properties-form';
import {
  ConversationEntityContext,
  useConversationEntity,
} from './conversation-entity-context';
import {
  ConversationPropertiesFormContext,
  ConversationPropertiesFormContextValue,
} from './conversation-properties-form-context';
import { useConversationPropertiesSubmit } from './use-conversation-properties-submit';

const ConversationPropertiesFormProvider = ({ children }: { children: ReactNode }) => {
  const { conversation, canEdit } = useConversationEntity();

  const { conversationPropertiesFormFields } = useConversationPropertiesForm(
    conversation,
    !canEdit,
  );
  const onSubmit = useConversationPropertiesSubmit();

  const value = useMemo<ConversationPropertiesFormContextValue>(
    () => ({
      fieldsByName: new Map(
        conversationPropertiesFormFields.map((field) => [field.name, field]),
      ),
      onSubmit,
    }),
    [conversationPropertiesFormFields, onSubmit],
  );

  return (
    <ConversationPropertiesFormContext.Provider value={value}>
      {children}
    </ConversationPropertiesFormContext.Provider>
  );
};

interface ConversationSidePanelProviderProps {
  conversation: Conversation;
  onUpdate: (conversation: Conversation) => Promise<void>;
  children: ReactNode;
}

export const ConversationSidePanelProvider = ({
  conversation,
  onUpdate,
  children,
}: ConversationSidePanelProviderProps) => {
  const canEdit = useCanUpdateConversation({
    conversationStatusId: conversation.conversationStatusId,
    isDeleted: conversation.isDeleted,
  });

  const entityValue = useMemo(
    () => ({ conversation, onUpdate, canEdit }),
    [conversation, onUpdate, canEdit],
  );

  return (
    <ConversationEntityContext.Provider value={entityValue}>
      <ConversationPropertiesFormProvider>
        <CustomAttributesSidePanelProvider
          entity="Conversation"
          entityId={conversation.conversationId}
          partnerId={conversation.partnerId}
          canEdit={canEdit}
        >
          {children}
        </CustomAttributesSidePanelProvider>
      </ConversationPropertiesFormProvider>
    </ConversationEntityContext.Provider>
  );
};
